import { useMemo, useState, useEffect } from 'react';

import { collection, orderBy, Query, query, doc, getDoc } from 'firebase/firestore';

import { useCollectionData } from 'react-firebase-hooks/firestore';

import { useData } from '../contextProviders/DataProvider';
import { firestore } from '../firebase';
import Course from '../types/Course';

interface UseCoursesProps {
  courseIdList?: string[];
}

export default function useCourses(props: UseCoursesProps) {
  // if courseIdList is provided, we assume the caller has permission and directly fetch the courses. If not provided, we do a query.
  const { courseIdList } = props;
  const { userState } = useData();
  const [individualCourses, setIndividualCourses] = useState<Course[]>([]);
  const [isIndividualLoading, setIsIndividualLoading] = useState(false);
  const [individualError, setIndividualError] = useState<Error | null>(null);

  // Stabilize on array contents so a new-reference-same-IDs array doesn't re-fetch.
  const courseIdListKey = courseIdList?.join(',');
  const memoizedCourseIdList = useMemo(
    () => courseIdList,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [courseIdListKey],
  );

  // Fetch individual documents when courseIdList is provided
  useEffect(() => {
    if (!memoizedCourseIdList?.length || userState.status !== 'ready' || !userState.userProfile) {
      return;
    }

    const fetchCourses = async () => {
      setIsIndividualLoading(true);
      setIndividualError(null);

      try {
        const coursePromises = memoizedCourseIdList.map(async (courseId) => {
          const courseDoc = await getDoc(doc(firestore, 'course', courseId));
          if (courseDoc.exists()) {
            return courseDoc.data() as Course;
          }
          return null;
        });

        const courseResults = await Promise.all(coursePromises);
        const validCourses = courseResults.filter((course): course is Course => course !== null);

        // Sort by name to match the original behavior
        validCourses.sort((a, b) => a.name.localeCompare(b.name));

        setIndividualCourses(validCourses);
      } catch (error) {
        setIndividualError(error as Error);
      } finally {
        setIsIndividualLoading(false);
      }
    };

    fetchCourses();
  }, [memoizedCourseIdList, userState.userProfile, userState.status]);

  const coursesQuery = useMemo(() => {
    if (userState.status !== 'ready' || !userState.userProfile) return null;

    // Collection query only when caller explicitly passes no courseIdList (CourseDirectory).
    // An empty array means "no courses yet" — don't fetch 11k docs while the event loads.
    if (courseIdList === undefined) {
      return query(collection(firestore, 'course') as Query<Course>, orderBy('name'));
    }

    return null;
  }, [courseIdList, userState.userProfile, userState.status]);

  const [courses, isCoursesLoading, coursesError] = useCollectionData<Course>(coursesQuery);

  if (userState.status === 'logged_out') {
    return {
      courses: null,
      isCoursesLoading: false,
      coursesError: new Error('User must be logged in to view courses'),
    };
  }

  // Return individual courses when courseIdList is provided, otherwise return query results
  if (courseIdList !== undefined) {
    return {
      courses: individualCourses,
      isCoursesLoading:
        isIndividualLoading ||
        userState.status === 'authenticating_user' ||
        userState.status === 'authenticating_profile' ||
        userState.status === 'authenticating_memberships',
      coursesError: individualError,
    };
  } else {
    return {
      courses: courses ?? [],
      isCoursesLoading:
        isCoursesLoading ||
        userState.status === 'authenticating_user' ||
        userState.status === 'authenticating_profile' ||
        userState.status === 'authenticating_memberships',
      coursesError,
    };
  }
}
